import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";
import ImportExportBar from "./ImportExportBar";
import CustomerSelect from "./CustomerSelect";

const emptyForm = {
  brandId: "",
  name: "",
  productId: "",
  minDistinctCustomers: "1",
  qtyPerCustomer: "",
  rewardType: "PERCENTAGE",
  rewardValue: "",
  validFrom: "",
  validTo: "",
  status: "ACTIVE",
};

const statusBadgeClass = { ACTIVE: "badge-green", INACTIVE: "badge-gray", EXPIRED: "badge-red" };

const Schemes = () => {
  const [schemes, setSchemes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [manageScheme, setManageScheme] = useState(null);
  const [qualifyingEntries, setQualifyingEntries] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [manageLoading, setManageLoading] = useState(false);
  const [entryForm, setEntryForm] = useState({ qualifyingCustomerId: "", quantity: "", entryDate: "" });
  const [recalculating, setRecalculating] = useState(false);
  const [fulfillEntryId, setFulfillEntryId] = useState(null);
  const [fulfillForm, setFulfillForm] = useState({ fulfillmentCustomerId: "", invoiceDate: "", amountCollected: "" });

  const brandById = useMemo(() => new Map(brands.map((b) => [b.id, b.name])), [brands]);
  const productById = useMemo(() => new Map(products.map((p) => [p.id, p.name])), [products]);
  const customerById = useMemo(() => new Map(customers.map((c) => [c.id, c.name])), [customers]);

  const loadOptions = async () => {
    try {
      const [brandsData, productsData, customersData] = await Promise.all([
        api.get("/api/v1/brands", { size: 200 }),
        api.get("/api/v1/products", { size: 200 }),
        api.getAll("/api/v1/customers"),
      ]);
      setBrands(brandsData?.content || []);
      setProducts(productsData?.content || []);
      setCustomers(customersData?.content || []);
    } catch (error) {
      toast.error(error.message || "Failed to load options");
    }
  };

  const loadSchemes = async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/schemes", { size: 200, status: statusFilter || undefined });
      setSchemes(data?.content || []);
    } catch (error) {
      toast.error(error.message || "Failed to load schemes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOptions();
  }, []);

  useEffect(() => {
    loadSchemes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (scheme) => {
    setEditingId(scheme.id);
    setFormData({
      brandId: scheme.brandId ?? "",
      name: scheme.name || "",
      productId: scheme.productId ?? "",
      minDistinctCustomers: String(scheme.minDistinctCustomers ?? "1"),
      qtyPerCustomer: String(scheme.qtyPerCustomer ?? ""),
      rewardType: scheme.rewardType || "PERCENTAGE",
      rewardValue: String(scheme.rewardValue ?? ""),
      validFrom: scheme.validFrom || "",
      validTo: scheme.validTo || "",
      status: scheme.status || "ACTIVE",
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        brandId: formData.brandId ? Number(formData.brandId) : null,
        name: formData.name,
        productId: Number(formData.productId),
        minDistinctCustomers: Number(formData.minDistinctCustomers),
        qtyPerCustomer: Number(formData.qtyPerCustomer),
        rewardType: formData.rewardType,
        rewardValue: Number(formData.rewardValue),
        validFrom: formData.validFrom,
        validTo: formData.validTo,
        status: formData.status,
      };
      if (editingId) {
        await api.put(`/api/v1/schemes/${editingId}`, payload);
        toast.success("Scheme updated");
      } else {
        await api.post("/api/v1/schemes", payload);
        toast.success("Scheme created");
      }
      setShowModal(false);
      loadSchemes();
    } catch (error) {
      toast.error(error.message || "Failed to save scheme");
    } finally {
      setSaving(false);
    }
  };

  const openManage = async (scheme) => {
    setManageScheme(scheme);
    setManageLoading(true);
    try {
      const [entriesData, achievementsData] = await Promise.all([
        api.get(`/api/v1/schemes/${scheme.id}/qualifying-entries`, { size: 100 }),
        api.get(`/api/v1/schemes/${scheme.id}/achievements`, { size: 100 }),
      ]);
      setQualifyingEntries(entriesData?.content || []);
      setAchievements(achievementsData?.content || []);
    } catch (error) {
      toast.error(error.message || "Failed to load scheme details");
    } finally {
      setManageLoading(false);
    }
  };

  const refreshManage = () => manageScheme && openManage(manageScheme);

  const handleAddEntry = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/api/v1/schemes/${manageScheme.id}/qualifying-entries`, {
        qualifyingCustomerId: Number(entryForm.qualifyingCustomerId),
        quantity: Number(entryForm.quantity),
        entryDate: entryForm.entryDate,
      });
      toast.success("Qualifying entry recorded");
      setEntryForm({ qualifyingCustomerId: "", quantity: "", entryDate: "" });
      refreshManage();
    } catch (error) {
      toast.error(error.message || "Failed to record entry");
    }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      await api.post(`/api/v1/schemes/${manageScheme.id}/recalculate`);
      toast.success("Achievement recalculated");
      refreshManage();
    } catch (error) {
      toast.error(error.message || "Failed to recalculate");
    } finally {
      setRecalculating(false);
    }
  };

  const handleFulfill = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/api/v1/schemes/qualifying-entries/${fulfillEntryId}/fulfill`, {
        fulfillmentCustomerId: Number(fulfillForm.fulfillmentCustomerId),
        invoiceDate: fulfillForm.invoiceDate || null,
        amountCollected: fulfillForm.amountCollected ? Number(fulfillForm.amountCollected) : null,
      });
      toast.success("Fulfillment assigned — invoice generated");
      setFulfillEntryId(null);
      setFulfillForm({ fulfillmentCustomerId: "", invoiceDate: "", amountCollected: "" });
      refreshManage();
    } catch (error) {
      toast.error(error.message || "Failed to assign fulfillment");
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading schemes...</div>
          </div>
        </div>
      ) : null}

      <div className="filters-bar">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
          <option value="EXPIRED">Expired</option>
        </select>
        <button className="primary" onClick={openCreate}>
          + New Scheme
        </button>
        <ImportExportBar
          importPath="/api/v1/schemes/import"
          exportPath="/api/v1/schemes/export"
          exportFilename="scheme-performance.xlsx"
          extraParams={{ status: statusFilter || undefined }}
          label="Schemes"
          onImported={loadSchemes}
        />
      </div>

      <div className="table-wrapper" style={{ overflowX: "auto" }}>
        <table style={{ minWidth: "1300px", whiteSpace: "nowrap" }}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Brand</th>
              <th>Product</th>
              <th>Min Customers</th>
              <th>Qty/Customer</th>
              <th>Reward</th>
              <th>Valid</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {schemes.length === 0 ? (
              <tr>
                <td colSpan={10}>No schemes found.</td>
              </tr>
            ) : (
              schemes.map((scheme) => (
                <tr key={scheme.id}>
                  <td>{scheme.id}</td>
                  <td>{scheme.name}</td>
                  <td>{scheme.brandName || brandById.get(scheme.brandId) || "-"}</td>
                  <td>{scheme.productName || productById.get(scheme.productId)}</td>
                  <td>{scheme.minDistinctCustomers}</td>
                  <td>{scheme.qtyPerCustomer}</td>
                  <td>
                    {scheme.rewardValue} {scheme.rewardType === "PERCENTAGE" ? "%" : "qty"}
                  </td>
                  <td>
                    {scheme.validFrom} → {scheme.validTo}
                  </td>
                  <td>
                    <span className={`badge ${statusBadgeClass[scheme.status] || "badge-gray"}`}>{scheme.status}</span>
                  </td>
                  <td className="action-buttons flex gap-2">
                    <button className="secondary" onClick={() => openEdit(scheme)}>
                      Edit
                    </button>
                    <button className="secondary" onClick={() => openManage(scheme)}>
                      Manage
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">{editingId ? "Edit Scheme" : "Create Scheme"}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                  <label>Name</label>
                  <input name="name" value={formData.name} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Brand (optional)</label>
                  <select name="brandId" value={formData.brandId} onChange={handleChange}>
                    <option value="">Any</option>
                    {brands.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Product</label>
                  <select name="productId" value={formData.productId} onChange={handleChange} required>
                    <option value="">Select product</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.skuCode})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Min Distinct Customers</label>
                  <input type="number" min="1" name="minDistinctCustomers" value={formData.minDistinctCustomers} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Qty per Customer</label>
                  <input type="number" min="0" step="0.001" name="qtyPerCustomer" value={formData.qtyPerCustomer} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Reward Type</label>
                  <select name="rewardType" value={formData.rewardType} onChange={handleChange}>
                    <option value="PERCENTAGE">Percentage</option>
                    <option value="FIXED_QTY">Fixed Quantity</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Reward Value</label>
                  <input type="number" min="0" step="0.001" name="rewardValue" value={formData.rewardValue} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Valid From</label>
                  <input type="date" onClick={(e) => e.target.showPicker?.()} name="validFrom" value={formData.validFrom} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Valid To</label>
                  <input type="date" onClick={(e) => e.target.showPicker?.()} name="validTo" value={formData.validTo} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select name="status" value={formData.status} onChange={handleChange}>
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                    <option value="EXPIRED">Expired</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {manageScheme ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setManageScheme(null)}>
          <div className="modal-box" style={{ maxWidth: "920px" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Manage — {manageScheme.name}</h3>
              <button className="close-btn" onClick={() => setManageScheme(null)}>
                Close
              </button>
            </div>

            {manageLoading ? (
              <div className="page-subtitle">Loading...</div>
            ) : (
              <>
                <h4 style={{ fontWeight: 600, marginBottom: "8px" }}>Qualifying Entries</h4>
                <form onSubmit={handleAddEntry} className="form-grid" style={{ gridTemplateColumns: "2fr 1fr 1fr auto", marginBottom: "10px" }}>
                  <CustomerSelect
                    customers={customers}
                    value={entryForm.qualifyingCustomerId}
                    onChange={(value) => setEntryForm((prev) => ({ ...prev, qualifyingCustomerId: value }))}
                    required
                    style={{ gridColumn: "1 / -1" }}
                  />
                  <input
                    type="number"
                    step="0.001"
                    placeholder="Qty"
                    value={entryForm.quantity}
                    onChange={(e) => setEntryForm((prev) => ({ ...prev, quantity: e.target.value }))}
                    required
                  />
                  <input
                    type="date"
                    onClick={(e) => e.target.showPicker?.()}
                    value={entryForm.entryDate}
                    onChange={(e) => setEntryForm((prev) => ({ ...prev, entryDate: e.target.value }))}
                    required
                  />
                  <button type="submit" className="primary">
                    Add
                  </button>
                </form>

                <div className="table-wrapper" style={{ marginTop: 0 }}>
                  <table>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Customer</th>
                        <th>Qty</th>
                        <th>Date</th>
                        <th>Reversed</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {qualifyingEntries.length === 0 ? (
                        <tr>
                          <td colSpan={6}>No qualifying entries yet.</td>
                        </tr>
                      ) : (
                        qualifyingEntries.map((entry) => (
                          <tr key={entry.id}>
                            <td>{entry.id}</td>
                            <td>{customerById.get(entry.qualifyingCustomerId) || entry.qualifyingCustomerId}</td>
                            <td>{entry.quantity}</td>
                            <td>{entry.entryDate}</td>
                            <td>{entry.reversed ? "Yes" : "No"}</td>
                            <td>
                              <button className="secondary" onClick={() => setFulfillEntryId(entry.id)}>
                                Fulfill
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between" style={{ marginTop: "20px" }}>
                  <h4 style={{ fontWeight: 600 }}>Achievements</h4>
                  <button className="secondary" onClick={handleRecalculate} disabled={recalculating}>
                    {recalculating ? "Recalculating..." : "Recalculate"}
                  </button>
                </div>
                <div className="table-wrapper" style={{ marginTop: "8px" }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Period</th>
                        <th>Distinct Customers</th>
                        <th>Achieved</th>
                        <th>Reward Qty Earned</th>
                        <th>Reward Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {achievements.length === 0 ? (
                        <tr>
                          <td colSpan={5}>No achievements yet.</td>
                        </tr>
                      ) : (
                        achievements.map((a) => (
                          <tr key={a.id}>
                            <td>
                              {a.periodStart} → {a.periodEnd}
                            </td>
                            <td>{a.distinctQualifyingCustomerCount}</td>
                            <td>{a.achieved ? "Yes" : "No"}</td>
                            <td>{a.rewardQuantityEarned}</td>
                            <td>
                              <span className="badge badge-blue">{a.rewardStatus}</span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      {fulfillEntryId ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setFulfillEntryId(null)}>
          <div className="modal-box" style={{ maxWidth: "480px" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Assign Fulfillment</h3>
              <button className="close-btn" onClick={() => setFulfillEntryId(null)}>
                Close
              </button>
            </div>
            <form onSubmit={handleFulfill}>
              <div className="form-grid" style={{ gridTemplateColumns: "1fr" }}>
                <div className="form-group">
                  <label>Fulfillment Customer</label>
                  <CustomerSelect
                    customers={customers}
                    value={fulfillForm.fulfillmentCustomerId}
                    onChange={(value) => setFulfillForm((prev) => ({ ...prev, fulfillmentCustomerId: value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Invoice Date (optional)</label>
                  <input
                    type="date"
                    onClick={(e) => e.target.showPicker?.()}
                    value={fulfillForm.invoiceDate}
                    onChange={(e) => setFulfillForm((prev) => ({ ...prev, invoiceDate: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Amount Collected (optional)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={fulfillForm.amountCollected}
                    onChange={(e) => setFulfillForm((prev) => ({ ...prev, amountCollected: e.target.value }))}
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setFulfillEntryId(null)}>
                  Cancel
                </button>
                <button type="submit" className="primary">
                  Assign
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Schemes;
